/**
 * Send-confirmation sound — a short synthesized "whoosh" (WebAudio, no asset).
 *
 * Autoplay policy: an AudioContext created outside a user gesture starts
 * suspended and can't be resumed programmatically. The undo-send countdown
 * fires the actual send from a setTimeout (NOT a gesture), so callers must
 * warmSendSound() inside the click that arms the send — the shared context is
 * then already running when playSendSound() fires seconds later.
 *
 * Everything is best-effort: audio failure must never affect the send.
 */

let ctx: AudioContext | null = null;

export function warmSendSound(): void {
  try {
    const AC: typeof AudioContext | undefined =
      (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    if (!ctx) ctx = new AC();
    if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
  } catch { /* no audio — fine */ }
}

export function playSendSound(): void {
  try {
    warmSendSound();
    if (!ctx || ctx.state !== 'running') return;
    const t0 = ctx.currentTime;
    const out = ctx.createGain();
    out.gain.value = 1;
    out.connect(ctx.destination);

    // Rising sine sweep — the "message leaving" whoosh.
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(320, t0);
    osc.frequency.exponentialRampToValueAtTime(980, t0 + 0.16);
    oscGain.gain.setValueAtTime(0.0001, t0);
    oscGain.gain.exponentialRampToValueAtTime(0.10, t0 + 0.02);
    oscGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.26);
    osc.connect(oscGain).connect(out);
    osc.start(t0);
    osc.stop(t0 + 0.3);

    // A soft fifth above, slightly delayed — makes it read "done ✓" rather
    // than a plain slide.
    const osc2 = ctx.createOscillator();
    const osc2Gain = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(1470, t0 + 0.12);
    osc2Gain.gain.setValueAtTime(0.0001, t0 + 0.12);
    osc2Gain.gain.exponentialRampToValueAtTime(0.05, t0 + 0.15);
    osc2Gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.34);
    osc2.connect(osc2Gain).connect(out);
    osc2.start(t0 + 0.12);
    osc2.stop(t0 + 0.36);
  } catch { /* no audio — fine */ }
}
